import type React from "react";
import type {RouteKey} from "../../routing/routes.ts";
import BarChartIcon from '../icons/svg/BarChartIcon.svg?react';
import DatabaseIcon from '../icons/svg/DatabaseIcon.svg?react';
import DeliveryBoxIcon from '../icons/svg/DeliveryBoxIcon.svg?react';
import DocumentIcon from '../icons/svg/DocumentIcon.svg?react';
import FlowChartIcon from '../icons/svg/FlowChartIcon.svg?react';
import LineChartIcon from '../icons/svg/LineChartIcon.svg?react';
import PieChartIcon from '../icons/svg/PieChartIcon.svg?react';
import SearchBoxIcon from '../icons/svg/SearchBoxIcon.svg?react';
import StoreIcon from '../icons/svg/StoreIcon.svg?react';
import AutoCategorizationIcon from '../icons/svg/AutoCategorizationIcon.svg?react';
import BotFlowIcon from '../icons/svg/BotFlowIcon.svg?react';
import AtlasIcon from '../icons/svg/AtlasIcon.svg?react';
import {TrendingUp} from "@mui/icons-material";
import ChatConstants from "../../pages/chat-agent/ChatConstants.ts";

export type CardConfig = {
    /**
     * The route to which the card navigates when clicked.
     * If false the link will not work.
     */
    route: RouteKey
    demoRoute?: RouteKey
    title: string
    icon?: React.ReactNode

    /**
     * Card that require activation are not available by default
     * They need to be explicitly enabled the environment file
     */
    defaultDisabled?: boolean

    /**
     * Card that will not be available for sample data bags
     */
    sampleDisabled?: boolean

    cardDescription?: React.ReactNode
}

const cardConfigs = {
    'koi-key-data-facts': {
        title: 'Explore Categories',
        route: 'analytics_koi_kdf',
        icon: <SearchBoxIcon/>,
        cardDescription: 'Get a top-level visibility into category spend',
        sampleDisabled: true,
    },
    'categorization-review': {
        title: 'Auto Categorization',
        route: 'cleanup_categorization',
        icon: <AutoCategorizationIcon/>,
        cardDescription: 'Auto-categorize spend with high accuracy',
    },
    'taxonomy-builder': {
        title: 'Taxonomy Builder',
        route: 'cleanup_taxonomy_builder',
        icon: <FlowChartIcon/>,
        cardDescription: 'Create and adjust spend taxonomies with full control',
    },
    'koi-ppv': {
        title: 'Purchase Price Variances',
        route: 'analytics_koi_ppv',
        icon: <LineChartIcon/>,
        cardDescription: 'Identify spend fragmentation across all categories',
        sampleDisabled: true,
    },
    'koi-spend-concentration': {
        title: 'Spend Concentration',
        route: 'analytics_koi_sc',
        icon: <BarChartIcon/>,
        cardDescription: 'Identify spend fragmentation across all categories',
        sampleDisabled: true,
    },
    'koi-spend-segmentation': {
        title: 'Spend Segmentation',
        route: 'analytics_koi_ss',
        // icon: null,
        defaultDisabled: true,
    },
    'supplier-normalization': {
        title: 'Supplier Normalization',
        route: 'cleanup_supplier_normalization',
        icon: <StoreIcon/>,
        cardDescription: 'Unify suppliers into a clean vendor master',
        sampleDisabled: true,
    },
    'adv-dashboard': {
        title: 'Opportunity Dashboard',
        route: 'analytics_dashboard',
        icon: <PieChartIcon/>,
        cardDescription: 'Identify savings and optimization opportunities',
        sampleDisabled: true,
    },
    'taxonomy-mapper': {
        title: 'Taxonomy Mapper',
        route: 'demo_merge_taxonomy_mapper',
        // icon: null,
        defaultDisabled: true,
    },
    'taxonomy-suggester': {
        title: 'Taxonomy Suggester',
        route: 'demo_cleanup_taxonomy_suggestion_v2',
        icon: <BotFlowIcon/>,
        cardDescription: 'AI suggests taxonomy based on your data and industry benchmarks',
        defaultDisabled: true,
    },
    'golden-record': {
        title: 'Material Harmonization',
        route: 'cleanup_golden_record',
        icon: <DeliveryBoxIcon/>,
        cardDescription: 'Identify & consolidate similar materials across all sources',
        defaultDisabled: true,
        sampleDisabled: true,
    },
    'contract-insights': {
        title: 'Contract Parser',
        route: 'cleanup_contract_insights',
        icon: <DocumentIcon/>,
        cardDescription: 'Extract structured data from contracts',
        defaultDisabled: true,
    },
    'chat': {
        title: ChatConstants.name,
        route: 'chat',
        icon: <AtlasIcon/>,
        defaultDisabled: true,
        sampleDisabled: true,
    },


    'sit': {
        title: 'Spend Initiative Tracker',
        route: 'sit_home',
        icon: <TrendingUp/>,
        defaultDisabled: true,
    },
    'data-ingestion': {
        title: 'Data Ingestion',
        route: 'data_ingestion',
        demoRoute: 'data_upload',
        icon: <DatabaseIcon/>,
        defaultDisabled: true,
    },
    'taxonomy-ingestion': {
        title: 'Taxonomy Ingestion',
        route: 'taxonomy_ingestion',
        icon: <DatabaseIcon/>,
        defaultDisabled: true,
    },
} satisfies Record<string, CardConfig>

export type DashboardCard = keyof typeof cardConfigs; // extract keys
export const CARD_CONFIG: Record<DashboardCard, CardConfig> = cardConfigs; // expose type
